import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	FormControlLabel,
	Grid,
	InputAdornment,
	MenuItem,
	Switch,
	TextField,
	Typography,
} from '@mui/material';
import { api } from 'services/api';
import {
	getPgFxSettingsRequest,
	updatePgFxSettingsRequest,
} from 'redux/reducers/pgSettings/reducer';
import {
	selectPgFxSettings,
	selectPgFxSettingsLoading,
	selectPgSaveLoading,
} from 'redux/reducers/pgSettings/selectors';
import {
	TPgFxRounding,
	TPgFxSettings,
	TPgFxSettingsUpdateRequest,
} from 'services/api/paymentGateway/types';
import { PgPage } from '../common/PgPage';
import { PgPanel } from '../common/PgPanel';
import { LegalEntitySelect } from '../common/LegalEntitySelect';
import { usePgLegalEntities } from '../common/hooks';
import { usePgCall } from '../common/usePgCall';
import {
	PG_FRACTION_DECIMALS,
	PG_FX_ROUNDING_OPTIONS,
	PG_MAX_PERCENT,
	PG_MAX_QUOTE_TTL_SECONDS,
	PG_MIN_QUOTE_TTL_SECONDS,
	PG_SETTINGS_GROUP,
} from '../constants';
import {
	countDecimals,
	formatDateTime,
	formatMoney,
	formatPercent,
	fractionToPercentInput,
	parseDecimal,
	percentInputToFraction,
	roundTo,
	roundUp2,
} from '../utils';

type TFxFormValues = {
	marginPercent: string;
	fixedMarkup: string;
	rounding: TPgFxRounding;
	quoteTtlSeconds: string;
	autoConvertEnabled: boolean;
	minConversionAmount: string;
};

type TFxFormErrors = Partial<Record<keyof TFxFormValues, string>>;

const toValues = (settings: TPgFxSettings): TFxFormValues => ({
	marginPercent: fractionToPercentInput(settings.marginPercent),
	fixedMarkup: String(settings.fixedMarkup),
	rounding: settings.rounding,
	quoteTtlSeconds: String(settings.quoteTtlSeconds),
	autoConvertEnabled: settings.autoConvertEnabled,
	minConversionAmount: String(settings.minConversionAmount),
});

const validate = (values: TFxFormValues): TFxFormErrors => {
	const errors: TFxFormErrors = {};
	const margin = parseDecimal(values.marginPercent);
	if (margin === null || margin < 0 || margin >= PG_MAX_PERCENT) {
		errors.marginPercent = `0 ≤ x < ${PG_MAX_PERCENT}`;
	} else if (countDecimals(values.marginPercent) > PG_FRACTION_DECIMALS - 2) {
		errors.marginPercent = `At most ${PG_FRACTION_DECIMALS - 2} decimals`;
	}
	const money = (field: 'fixedMarkup' | 'minConversionAmount') => {
		const value = parseDecimal(values[field]);
		if (value === null || value < 0) errors[field] = 'Must be ≥ 0';
		else if (countDecimals(values[field]) > 2) errors[field] = 'At most 2 decimals';
	};
	money('fixedMarkup');
	money('minConversionAmount');
	const ttl = Number(values.quoteTtlSeconds);
	if (
		!/^\d+$/.test(values.quoteTtlSeconds.trim()) ||
		ttl < PG_MIN_QUOTE_TTL_SECONDS ||
		ttl > PG_MAX_QUOTE_TTL_SECONDS
	) {
		errors.quoteTtlSeconds = `${PG_MIN_QUOTE_TTL_SECONDS}–${PG_MAX_QUOTE_TTL_SECONDS} seconds`;
	}
	return errors;
};

const toRequest = (values: TFxFormValues): TPgFxSettingsUpdateRequest => ({
	marginPercent: percentInputToFraction(values.marginPercent) ?? 0,
	fixedMarkup: parseDecimal(values.fixedMarkup) ?? 0,
	rounding: values.rounding,
	quoteTtlSeconds: Number(values.quoteTtlSeconds),
	autoConvertEnabled: values.autoConvertEnabled,
	minConversionAmount: parseDecimal(values.minConversionAmount) ?? 0,
});

/**
 * Settings → Direct Debit FX (decision 11, 03 §4.5): the GBP amount of a Direct Debit is the
 * Airwallex rate × (1 + margin) + fixed markup, rounded; GBP received is converted to EUR
 * automatically once settled.
 */
export const DdFx: FC = () => {
	const dispatch = useDispatch();
	const { legalEntities } = usePgLegalEntities();
	const settings = useSelector(selectPgFxSettings);
	const loading = useSelector(selectPgFxSettingsLoading);
	const saving = useSelector(selectPgSaveLoading('fxSettings'));

	const [legalEntityId, setLegalEntityId] = useState<number | null>(null);
	const [values, setValues] = useState<TFxFormValues | null>(null);
	const [previewAmount, setPreviewAmount] = useState('100');

	const previewCall = usePgCall(api.paymentGateway.previewFx, {
		fallbackError: 'Failed to get a quote from Airwallex',
	});

	useEffect(() => {
		if (legalEntityId !== null) dispatch(getPgFxSettingsRequest(legalEntityId));
	}, [dispatch, legalEntityId]);

	const current = settings && settings.legalEntityId === legalEntityId ? settings : null;

	// Every new settings object (loaded or returned by a save) resets the form.
	const [syncedFrom, setSyncedFrom] = useState<typeof current | undefined>(undefined);
	if (current !== syncedFrom) {
		setSyncedFrom(current);
		setValues(current ? toValues(current) : null);
	}

	const errors = useMemo(() => (values ? validate(values) : {}), [values]);
	const hasErrors = Object.keys(errors).length > 0;
	const changed = useMemo(() => {
		if (!values || !current) return false;
		const saved = toValues(current);
		return (Object.keys(saved) as (keyof TFxFormValues)[]).some(
			(key) => String(saved[key]) !== String(values[key]),
		);
	}, [values, current]);

	const setField = <K extends keyof TFxFormValues>(key: K, value: TFxFormValues[K]) =>
		setValues((v) => (v ? { ...v, [key]: value } : v));

	const handleSave = () => {
		if (!values || !current || hasErrors) return;
		dispatch(
			updatePgFxSettingsRequest({ legalEntityId: current.legalEntityId, body: toRequest(values) }),
		);
	};

	const previewAmountEur = parseDecimal(previewAmount);
	const previewValid =
		previewAmountEur !== null && previewAmountEur > 0 && countDecimals(previewAmount) <= 2;

	const handlePreview = () => {
		if (!current || !previewValid || previewAmountEur === null) return;
		void previewCall.run({ legalEntityId: current.legalEntityId, amountEur: previewAmountEur });
	};

	const quote = previewCall.result;
	const ddCurrency = current?.ddCurrency ?? 'GBP';

	// The same Airwallex rate with the values on screen (approximate: the quoted rate is rounded).
	const draftEstimate = useMemo(() => {
		if (!quote || !values || hasErrors) return null;
		const request = toRequest(values);
		const raw = quote.amountEur * quote.awxRate * (1 + request.marginPercent) + request.fixedMarkup;
		const amount = request.rounding === 'up' ? roundUp2(raw) : roundTo(raw, 2);
		return { amount, rate: amount / quote.amountEur };
	}, [quote, values, hasErrors]);

	return (
		<PgPage
			title="Direct Debit FX"
			group={PG_SETTINGS_GROUP}
			actions={
				<LegalEntitySelect
					legalEntities={legalEntities}
					value={legalEntityId}
					onChange={setLegalEntityId}
					disabled={saving}
				/>
			}
		>
			<PgPanel
				title={`Rate for Direct Debit in ${ddCurrency}`}
				actions={
					<>
						{(loading || saving) && <CircularProgress size={18} />}
						<Button
							size="small"
							onClick={() => current && setValues(toValues(current))}
							disabled={!changed || saving}
						>
							Discard
						</Button>
						<Button
							size="small"
							variant="contained"
							onClick={handleSave}
							disabled={!values || (!changed && current?.id !== null) || hasErrors || saving}
						>
							Save
						</Button>
					</>
				}
			>
				{!values || !current ? (
					<Typography variant="body2" color="text.secondary">
						{loading || legalEntityId === null ? 'Loading…' : 'No data.'}
					</Typography>
				) : (
					<>
						{current.id === null && (
							<Alert severity="warning" sx={{ mb: 2 }}>
								Settings are not saved yet: quotes use the defaults shown below. Save them to fix
								the values.
							</Alert>
						)}
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6} md={4}>
								<TextField
									fullWidth
									size="small"
									label="Margin"
									value={values.marginPercent}
									onChange={(e) => setField('marginPercent', e.target.value)}
									error={!!errors.marginPercent}
									helperText={errors.marginPercent ?? 'Added to the Airwallex rate'}
									inputProps={{ inputMode: 'decimal' }}
									InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={4}>
								<TextField
									fullWidth
									size="small"
									label="Fixed markup"
									value={values.fixedMarkup}
									onChange={(e) => setField('fixedMarkup', e.target.value)}
									error={!!errors.fixedMarkup}
									helperText={errors.fixedMarkup ?? 'Added to every Direct Debit amount'}
									inputProps={{ inputMode: 'decimal' }}
									InputProps={{
										endAdornment: <InputAdornment position="end">{ddCurrency}</InputAdornment>,
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={4}>
								<TextField
									select
									fullWidth
									size="small"
									label="Rounding"
									value={values.rounding}
									onChange={(e) => setField('rounding', e.target.value as TPgFxRounding)}
									helperText=" "
								>
									{PG_FX_ROUNDING_OPTIONS.map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</TextField>
							</Grid>
							<Grid item xs={12} sm={6} md={4}>
								<TextField
									fullWidth
									size="small"
									label="Quote valid for"
									value={values.quoteTtlSeconds}
									onChange={(e) => setField('quoteTtlSeconds', e.target.value)}
									error={!!errors.quoteTtlSeconds}
									helperText={errors.quoteTtlSeconds ?? 'How long the payment page keeps the quote'}
									inputProps={{ inputMode: 'numeric' }}
									InputProps={{ endAdornment: <InputAdornment position="end">sec</InputAdornment> }}
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={4}>
								<TextField
									fullWidth
									size="small"
									label="Minimum amount to convert"
									value={values.minConversionAmount}
									onChange={(e) => setField('minConversionAmount', e.target.value)}
									error={!!errors.minConversionAmount}
									helperText={
										errors.minConversionAmount ?? 'Smaller amounts are not converted (alert)'
									}
									inputProps={{ inputMode: 'decimal' }}
									InputProps={{
										endAdornment: <InputAdornment position="end">{ddCurrency}</InputAdornment>,
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={4}>
								<FormControlLabel
									control={
										<Switch
											checked={values.autoConvertEnabled}
											onChange={(e) => setField('autoConvertEnabled', e.target.checked)}
										/>
									}
									label={`Convert received ${ddCurrency} to ${current.autoConvertTargetCurrency} automatically`}
								/>
							</Grid>
						</Grid>
						<Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
							Base currency {current.baseCurrency}, Direct Debit currency {current.ddCurrency}.
							{current.updatedAt
								? ` Last change: ${formatDateTime(current.updatedAt)}${current.updatedBy ? ` by ${current.updatedBy}` : ''}.`
								: ''}
						</Typography>
					</>
				)}
			</PgPanel>

			<PgPanel title="Live preview">
				<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
					<TextField
						size="small"
						label="Invoice amount"
						value={previewAmount}
						onChange={(e) => setPreviewAmount(e.target.value)}
						error={!previewValid}
						helperText={previewValid ? ' ' : 'A positive amount with up to 2 decimals'}
						inputProps={{ inputMode: 'decimal' }}
						InputProps={{ endAdornment: <InputAdornment position="end">EUR</InputAdornment> }}
						sx={{ width: 180 }}
					/>
					<Button
						variant="outlined"
						onClick={handlePreview}
						disabled={!current || !previewValid || previewCall.loading}
						startIcon={previewCall.loading ? <CircularProgress size={16} /> : undefined}
					>
						Get quote
					</Button>
				</Box>
				{quote ? (
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>
							<Typography variant="subtitle2" color="primary">
								Gateway quote (saved settings, live Airwallex rate)
							</Typography>
							<Typography variant="body2">
								{formatMoney(quote.amountEur, 'EUR')} →{' '}
								<b>{formatMoney(quote.gbpAmount, ddCurrency)}</b>
								<br />
								Airwallex rate {quote.awxRate}, margin {formatPercent(quote.marginPercent)}, markup{' '}
								{formatMoney(quote.fixedMarkupGbp, ddCurrency)}, effective rate{' '}
								{quote.effectiveRate}
								<br />
								Quoted {formatDateTime(quote.quotedAt)}, valid until{' '}
								{formatDateTime(quote.expiresAt)}
							</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<Typography variant="subtitle2" color="primary">
								Same rate with the values on screen{changed ? ' (unsaved)' : ''}
							</Typography>
							{draftEstimate ? (
								<Typography variant="body2">
									{formatMoney(quote.amountEur, 'EUR')} →{' '}
									<b>≈ {formatMoney(draftEstimate.amount, ddCurrency)}</b>
									<br />
									Effective rate ≈ {roundTo(draftEstimate.rate, 6)}
								</Typography>
							) : (
								<Typography variant="body2" color="text.secondary">
									Fix the form errors to see the estimate.
								</Typography>
							)}
						</Grid>
					</Grid>
				) : (
					<Typography variant="body2" color="text.secondary">
						Press “Get quote” to fetch the live Airwallex rate.
					</Typography>
				)}
			</PgPanel>
		</PgPage>
	);
};
